import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';

import {
  newCheckInAction,
  newNextAction,
  NEW_NEXT,
  NEW_CHECKIN,
} from '../../actions/navigation';

// Components
import Header from '../../components/Header';
import Progress from '../../components/Progress';
import FormRow from '../../components/Form/FormRow';
import FormColumn from '../../components/Form/FormColumn';
import Transition from '../../components/Transition';
import Container from '../../components/Container';
import Headline from '../../components/Headline';
import ApplicationId from '../../components/ApplicationId';
import LoanOfferConfirmation from '../../components/LoanOfferConfirmation';

// Configuration & assets
import { useConfig } from '../../config/ConfigContext';
import { parseUrlParams, addLusaHeaderParam } from '../../utils/parseUrlParams';
import { ricWorkflowName } from '../../utils/appConstants';

const Confirmed = (props) => {
  const config = useConfig();
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [uploadingPercentage, setUploadingPercentage] = useState(0);
  const match = useParams();
  const { fetch, navigate, checkinData } = props;
  const hasDownPayment = useMemo(() => !!get(checkinData, 'data.data.downPaymentRequired'), [checkinData]);
  const workflowType = get(match, 'workflowType') === 'luwa' ? 'dtm-luwa' : get(match, 'workflowType');
  const dpOnlyApp = workflowType === 'dtm-luwa-dponly' || workflowType === ricWorkflowName;
  const ricWorkFlow = workflowType === ricWorkflowName;
  const ricOrLoan = ricWorkFlow ? 'retail installment contract' : 'loan';
  const isACH = useMemo(() => get(checkinData, 'data.data.isAchFlow'), [checkinData]);

  useEffect(() => {
    const params = parseUrlParams(window.location.search);

    if (isEmpty(checkinData)) {
      props.newCheckInAction({
        workflowType,
        key: params.key,
      });
    }

    if (
      !get(checkinData, 'data.data.returnUrl', get(checkinData, 'state.data.returnUrl'))
        && ((fetch.isDone[`${NEW_NEXT}`] && !fetch.isRunning[`${NEW_NEXT}`])
        || (fetch.isDone[`${NEW_CHECKIN}`] && !fetch.isRunning[`${NEW_CHECKIN}`]))
    ) {
      let routeUrl = get(checkinData, 'data.url', '').replace(config.hostUrl, '');
      if (routeUrl && params.lusa_header && params.lusa_header === 'false') {
        routeUrl = addLusaHeaderParam(routeUrl);
      }
      navigate(routeUrl);
    }
  }, [checkinData]);

  const handleSubmitFrom = (e) => {
    e.preventDefault();
    const params = parseUrlParams(window.location.search);

    setIsProgressVisible(true);
    props.newNextAction({
      workflowType,
      key: params.key,
      data: {
        returnUrl: `${window.location.origin}/agreement/complete?key=${params.key}`,
      },
      onUploadProgress: (progressEvent) => {
        setIsProgressVisible(true);
        setUploadingPercentage(Math.floor((progressEvent.loaded * 100) / progressEvent.total));
      },
    });
  };

  return (
    <Transition className="transition-item wrapper loan-options">
      <Header navigate={navigate} />
      {get(checkinData, 'attributes.entityId') ? (
        <ApplicationId id={get(checkinData, 'attributes.entityId')} style={{ marginTop: 10 }} />
      ) : null}
      <div className="text-center">
        <Headline
          header={(
            <>
              Offer Confirmation
            </>
          )}
        />
        <Container className="container">
          <FormRow>
            <FormColumn>
              {get(checkinData, 'data.data') && <LoanOfferConfirmation loanDetails={get(checkinData, 'data.data')} handleSubmitFrom={handleSubmitFrom} isDPOnly={dpOnlyApp} isACH={isACH} />}
            </FormColumn>
          </FormRow>
        </Container>
      </div>
      {dpOnlyApp && (
        <FormRow>
          <FormColumn>
            <div style={{ marginTop: 20 }}>
              By clicking
              {isACH
                ? ' “SUBMIT DOWN PAYMENT”, the down payment shown above will be debited from the bank account you provided.'
                : ' “CONTINUE TO ESIGN”, your Debit Card will be charged the down payment.'}
            </div>
          </FormColumn>
        </FormRow>
      )}
      <FormRow>
        <FormColumn>
          <div style={{ marginTop: 20 }}>
            <small>
              Before your application can be finalized, LendingUSA and/or
              {ricWorkFlow ? (
                <>
                  {' '}
                  {get(checkinData, 'data.data.businessName') ? `${get(checkinData, 'data.data.businessName')}, ` : null}
                  {get(checkinData, 'data.data.dba') ? ` dba ${get(checkinData, 'data.data.dba')} ` : null}
                </>
              )
                : (
                  <>
                    {' '}
                    one of its
                    {' '}
                    <a href="https://lendingusa.com/lending-partners/" style={{ textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">lending partners</a>
                    {' '}
                  </>
                )}
              will request a full credit report from one or more of the credit reporting agencies. This request will occur if you proceed past this point in the application. This request will appear as a hard inquiry on your credit report and may affect your credit score.
            </small>
          </div>
        </FormColumn>
      </FormRow>
      {!!Number(get(checkinData, 'data.data.zipTerm')) && (
        <FormRow>
          <FormColumn>
            <div style={{ marginTop: 20 }}>
              <small>
                * Your
                {' '}
                {ricOrLoan}
                {' '}
                may have a No Interest on Principal Option Promotion included. This promotion can save you money if you pay off the principal amount of the
                {' '}
                {ricOrLoan}
                {' '}
                in full within the Promotional Period (&quot;Promotional Period&quot;). During the Promotional Period you will be responsible for making all of your monthly payments and your
                {' '}
                {ricOrLoan}
                {' '}
                will accrue interest on a monthly basis. If you pay off your
                {' '}
                {ricOrLoan}
                {' '}
                within the Promotional Period, the monthly payments that you have made during this period, which includes accrued interest, will be deducted from the principal amount of the
                {' '}
                {ricOrLoan}
                . Length of Promotional Periods vary, please review your
                {' '}
                {ricWorkFlow ? 'contract' : 'loan agreement'}
                {' '}
                for full details.
              </small>
            </div>
          </FormColumn>
        </FormRow>
      )}
      {((get(checkinData, 'data.data.originationPercentage') !== 0) && !dpOnlyApp) && (
        <FormRow>
          <FormColumn>
            <div style={{ marginTop: 20, paddingBottom: 20 }}>
              <small>
                ** The Origination Fee may be up to 8% of the Amount Financed, is non-refundable, and is considered earned upon the funding of your loan. You can calculate the dollar amount of the Origination Fee for each offer listed above by multiplying the Amount Financed figure for that offer by .08 if the Origination Fee is 8%, by .05 if it is 5%, etc. For example, if the Origination Fee is 8% and if the Amount Financed is $1,000 the corresponding Origination Fee would be $80 ($1000 x .08 = $80). The total Principal Amount of your loan is the sum of the Amount Financed and the Origination Fee (i.e. if the Origination Fee is 8% and if the Amount Financed is $1,000 the Principal Amount of the loan would be $1080). Please review your Loan Agreement for additional terms and conditions.
              </small>
            </div>
          </FormColumn>
        </FormRow>
      )}
      {hasDownPayment ? (
        <FormRow>
          <FormColumn>
            <div style={{ marginTop: 20, paddingBottom: 20 }}>
              <small>
                ** In connection with your intended purchase, the merchant requires a down payment in the amount shown above. Your down payment to the merchant for your purchase is a condition to the fulfilment of your
                {' '}
                {ricWorkFlow ? 'financing' : 'loan'}
                {' '}
                offer.
              </small>
            </div>
          </FormColumn>
        </FormRow>
      ) : null}
      {
        isProgressVisible && <Progress percent={uploadingPercentage} />
      }
    </Transition>
  );
};

Confirmed.propTypes = {
  navigate: PropTypes.func.isRequired,
  checkinData: PropTypes.object.isRequired,
  fetch: PropTypes.object.isRequired,
  newCheckInAction: PropTypes.func.isRequired,
  newNextAction: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  fetch: state.fetch,
  checkinData: state.screen.checkinData,
});

const mapDispatchToProps = {
  newCheckInAction,
  newNextAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(Confirmed);
